"use client";

import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect,
  useMemo, useRef, useState, type ReactNode,
} from "react";
import {
  ANALYTICS_CONSENT_KEY, ANALYTICS_CONSENT_VERSION, ANALYTICS_ID,
  ANALYTICS_LIFETIME_SECONDS, analyticsReferrer,
  clearAnalyticsCookies, readAnalyticsConsent, type AnalyticsConsent, type AnalyticsPage,
} from "../lib/analytics";
import { SITE_URL } from "../lib/site";
import styles from "./analytics-consent.module.css";

type RegisteredPage = AnalyticsPage & { id: symbol };
const AnalyticsContext = createContext({
  registerPage: (_path: string, _title: string): (() => void) => () => undefined,
  openPreferences: () => undefined as void,
});

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const [page, setPage] = useState<RegisteredPage | null>(null);
  const [consent, setConsent] = useState<AnalyticsConsent | null>(null);
  const [ready, setReady] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [productionOrigin, setProductionOrigin] = useState("");
  const frame = useRef<HTMLIFrameElement>(null);
  const previousPublicPage = useRef("");
  const preferencesTrigger = useRef<HTMLElement | null>(null);
  const allowButton = useRef<HTMLButtonElement>(null);

  const stopTracking = useCallback(() => {
    const target = frame.current?.contentWindow;
    if (target) {
      // Stop even unload/engagement beacons before destroying the document.
      (target as Window & Record<string, unknown>)[`ga-disable-${ANALYTICS_ID}`] = true;
    }
  }, []);

  const registerPage = useCallback((path: string, title: string) => {
    const id = Symbol(path);
    const referrer = previousPublicPage.current || analyticsReferrer(document.referrer);
    setPage({ id, path, title, referrer });
    previousPublicPage.current = `${SITE_URL}${path}`;
    return () => {
      stopTracking();
      setPage(current => current?.id === id ? null : current);
    };
  }, [stopTracking]);

  useEffect(() => {
    const sync = () => {
      const stored = readAnalyticsConsent();
      if (stored?.choice !== "granted") {
        stopTracking();
        clearAnalyticsCookies();
      }
      setConsent(stored);
      setReady(true);
    };
    sync();
    // Local development, previews and browser tests never send real statistics.
    if (location.protocol === "https:" && ["kaizen.co.ke", "www.kaizen.co.ke"].includes(location.hostname)) {
      setProductionOrigin(location.origin);
    }
    const storage = (event: StorageEvent) => {
      if (event.key === ANALYTICS_CONSENT_KEY || event.key === null) sync();
    };
    window.addEventListener("storage", storage);
    return () => window.removeEventListener("storage", storage);
  }, [stopTracking]);

  useEffect(() => {
    if (!consent) return;
    // Recheck long-lived tabs without exceeding the browser's timer limit.
    const timer = window.setTimeout(() => {
      const stored = readAnalyticsConsent();
      if (stored?.choice !== "granted") {
        stopTracking();
        clearAnalyticsCookies();
      }
      setConsent(stored);
    }, Math.min(Math.max(consent.expiresAt - Date.now(), 0), 86_400_000));
    return () => window.clearTimeout(timer);
  }, [consent, stopTracking]);

  const openPreferences = useCallback(() => {
    preferencesTrigger.current = document.activeElement as HTMLElement;
    setPreferencesOpen(true);
  }, []);
  useEffect(() => {
    if (preferencesOpen) allowButton.current?.focus();
  }, [preferencesOpen]);

  const choose = (choice: AnalyticsConsent["choice"]) => {
    if (choice === "denied") stopTracking();
    const record = {
      version: ANALYTICS_CONSENT_VERSION, choice,
      expiresAt: Date.now() + ANALYTICS_LIFETIME_SECONDS * 1000,
    };
    try { localStorage.setItem(ANALYTICS_CONSENT_KEY, JSON.stringify(record)); } catch { /* Session-only choice. */ }
    if (choice === "denied") clearAnalyticsCookies();
    setConsent(record);
    setPreferencesOpen(false);
    preferencesTrigger.current?.focus();
  };

  const enabled = ready && consent?.choice === "granted" && !!productionOrigin && !!page;
  const frameUrl = useMemo(() => page
    ? `/analytics-frame?${new URLSearchParams({ page: page.path, referrer: page.referrer })}` : "", [page]);

  useEffect(() => {
    if (!enabled) return;
    const scroll = () => {
      const height = document.documentElement.scrollHeight;
      if (window.scrollY > 0 && window.scrollY + window.innerHeight >= height * 0.9) {
        frame.current?.contentWindow?.postMessage("kaizen:analytics:scroll", productionOrigin);
      }
    };
    window.addEventListener("scroll", scroll, { passive: true });
    return () => window.removeEventListener("scroll", scroll);
  }, [enabled, page, productionOrigin]);

  const context = useMemo(() => ({ registerPage, openPreferences }), [registerPage, openPreferences]);
  return <AnalyticsContext.Provider value={context}>
    {children}
    {enabled && <iframe
      key={page.path} ref={frame} title="Optional website analytics" hidden aria-hidden="true"
      referrerPolicy="no-referrer" src={frameUrl}
    />}
    {ready && page && (!consent || preferencesOpen) && <section
      className={styles.banner} aria-label="Cookie preferences" aria-describedby="analytics-description"
    >
      <div>
        <h2>Optional cookies</h2>
        <p id="analytics-description">Optional Google Analytics cookies help us understand visits to our public pages. Rejecting them won&apos;t affect Kaizen. <a href="/privacy#section-6">Privacy Policy</a></p>
      </div>
      <div className={styles.actions}>
        <button ref={allowButton} type="button" onClick={() => choose("granted")}>Accept cookies</button>
        <button type="button" onClick={() => choose("denied")}>Reject cookies</button>
        {consent && <button type="button" onClick={() => {
          setPreferencesOpen(false);
          preferencesTrigger.current?.focus();
        }}>Close</button>}
      </div>
    </section>}
  </AnalyticsContext.Provider>;
}

// Mount only on a confirmed public screen, never while restoring a session.
export function PublicAnalyticsPage({ path, title }: { path: string; title: string }) {
  const { registerPage } = useContext(AnalyticsContext);
  useLayoutEffect(() => registerPage(path, title), [path, title, registerPage]);
  return null;
}

export function AnalyticsPreferencesButton() {
  const { openPreferences } = useContext(AnalyticsContext);
  return <button type="button" className={styles.preferences} onClick={openPreferences}>Cookie preferences</button>;
}
